import { Routes } from '@angular/router';
import { EnhancedAuthGuard } from 'src/app/core/guards/enhanced-auth.guard';
import { RouteData } from 'src/app/core/models/route-data';

/**
 * Routes that didn't fit the other domain files. Notable grouping reasons:
 * - GNE uses a nested named outlet (gneBenefitDetailsPeoplePicker) owned by GneSummaryComponent
 * - Producer onboarding has a parent-child structure with its own resolver
 * - Selling relationship and hierarchy routes share custom side rails
 */
export const REMAINING_ROUTES: Routes = [
    {
        path: 'setupSellingRelationship',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'setupSellingRelationship',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/producer-onboarding/setup-selling-relationship/setup-selling-relationship.component').then(m => m.SetupSellingRelationshipComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'modifySellingRelationship',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'modifySellingRelationship',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/selling-relationship/modify-selling-relationship-workflow/modify-selling-relationship-workflow.component').then(m => m.ModifySellingRelationshipWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/selling-relationship/modify-selling-relationship-workflow/selling-relationship-workflow-side-rail/selling-relationship-workflow-side-rail.component').then(m => m.SellingRelationshipWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'createWritingProfile',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'createWritingProfile',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/writing-profile/create-writing-profile-workflow/create-writing-profile-workflow.component').then(m => m.CreateWritingProfileWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/selling-relationship/modify-selling-relationship-workflow/selling-relationship-workflow-side-rail/selling-relationship-workflow-side-rail.component').then(m => m.SellingRelationshipWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'hierarchyDetail',
        loadComponent: () => import('src/components/hierarchy/hierarchy-detail/hierarchy-detail.component').then(m => m.HierarchyDetailComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData
    },

    {
        path: 'createHierarchy',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'createHierarchy',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/hierarchy/create-hierarchy/create-hierarchy.component').then(m => m.CreateHierarchyComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'updateHierarchy',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'updateHierarchy',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/hierarchy/update-hierarchy/update-hierarchy.component').then(m => m.UpdateHierarchyComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'modifyHierarchy',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'modifyHierarchy',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/hierarchy/modify-hierarchy/modify-hierarchy.component').then(m => m.ModifyHierarchyComponent),
                outlet: 'content'
            },
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'hierarchyDetailHistory',
        loadComponent: () => import('src/components/hierarchy/hierarchy-detail/history/hierarchy-detail-history.component').then(m => m.HierarchyDetailHistoryComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData
    },

    {
        path: 'producerOnboarding',
        loadComponent: () => import('src/components/producer-onboarding/producer-onboarding.component').then(m => m.ProducerOnboardingComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: 'profile/:entityType',
                loadComponent: () => import('src/components/producer-onboarding/profile/producer-onboarding-profile.component').then(m => m.ProducerOnboardingProfileComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'producerOnboardingProfile',
                    requiresPermission: true
                } satisfies RouteData
            }
        ]
    },

    {
        path: 'disclosureSummary',
        loadComponent: () => import('src/components/entity-record/disclosures/summary.component').then(m => m.DisclosureSummaryComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false,
            // Firm-specific article ID 14 existed in pre-FOF-7854 determineHelpPathForFirm(), which
            // was never called — firm users have always been served the advisors article. See FOF-7854.
            helpId: { advisors: 15 }
        } satisfies RouteData
    },

    {
        path: 'acqSummary',
        loadComponent: () => import('src/components/entity-record/acq/acq-summary.component').then(m => m.AcqSummaryComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false,
            // Firm-specific article ID 42 existed in pre-FOF-7854 determineHelpPathForFirm(), which
            // was never called — firm users have always been served the advisors article. See FOF-7854.
            helpId: { advisors: 43 }
        } satisfies RouteData
    },

    {
        path: 'acqHistorySummary',
        loadComponent: () => import('src/components/entity-record/acq/acq-summary.component').then(m => m.AcqSummaryComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData
    },

    {
        path: 'gneSummary',
        loadComponent: () => import('src/components/entity-record/gne/gne-summary.component').then(m => m.GneSummaryComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false,
            // Firm-specific article ID 48 existed in pre-FOF-7854 determineHelpPathForFirm(), which
            // was never called — firm users have always been served the advisors article. See FOF-7854.
            helpId: { advisors: 49 }
        } satisfies RouteData
    },

    {
        path: 'needToReportObas',
        loadComponent: () => import('src/components/need-to-report-obas/need-to-report-obas.component').then(m => m.NeedToReportObasComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData
    },

    {
        path: 'finraU4Popup',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'finraU4Popup',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/finra/finra-U4-popup.component').then(m => m.FinraU4PopupComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    {
        path: 'commercial-designations',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/commercial-designations/commercial-designations-workflow.component').then(m => m.CommercialDesignationsWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/commercial-designations/siderail/commercial-designations-side-rail.component').then(m => m.CommercialDesignationsSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    // Note: Same route path as commercial-designations but with query params
    // In Angular Router, this is handled via query params on the same route
    // Original route: commercial-designations-manage with ?stateCode&licenseNumber

    {
        path: 'freePremiumTrial',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'freePremiumTrial',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/free-premium-trial/free-premium-trial-offer/free-premium-trial-offer.component').then(m => m.FreePremiumTrialOfferComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/invite-signup/header/invite-signup-header.component').then(m => m.InviteSignupHeaderComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'subscriptionUpgradeBenefits',
        loadComponent: () => import('src/components/insurance/insurance.component').then(m => m.InsuranceComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'subscriptionUpgradeBenefits',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/subscription-upgrade-workflow/benefits-banner/subscription-upgrade-benefits-banner.component').then(m => m.SubscriptionUpgradeBenefitsBannerComponent),
                outlet: 'banner'
            },
            {
                path: '',
                loadComponent: () => import('src/components/subscription-upgrade-workflow/account-benefits/subscription-upgrade-account-benefits.component').then(m => m.SubscriptionUpgradeAccountBenefitsComponent),
                outlet: 'content'
            }
        ]
    },

    {
        path: 'viewContractFile',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'viewContractFile',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/contract-documents/view-contract-file/view-contract-file.component').then(m => m.ViewContractFileComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'viewCompScheduleFile',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/contract-documents/view-comp-schedule-file/view-comp-schedule-file.component').then(m => m.ViewCompScheduleFileComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'viewProducerContractDocument',
        loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'viewProducerContractDocument',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/contract-documents/view-producer-contract-document/view-producer-contract-document.component').then(m => m.ViewProducerContractDocumentComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'contractConfigurationVersionsDetails',
        loadComponent: () => import('src/components/configuration-console/contract-configuration-versions-details/contract-configuration-versions-details.component').then(m => m.ContractConfigurationVersionsDetailsComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData
    },

    {
        path: 'producerContractSigning',
        // NOT a workflow: this is a plain full-page holder for the DocuSign iframe. Use
        // HeaderContentContainerComponent (block-flow outlets), NOT MultiOutletContainerComponent.
        // The latter wraps content in a flex-row `.workflow-app-container`; even with an EmptyComponent
        // in the sideRail outlet, the flex row leaves a phantom side-rail-shaped gap beside the iframe
        // (issue #22, same root cause as the docusignLanding fix). The original UI-Router state had
        // parent: 'default' and only overrode sideRail (Empty) + content@, so it INHERITED the app
        // header and footer. The unified header still comes from app.component (this route is not in
        // usesNamedOutlets), but the footer was suppressed by usesCustomFooter with nothing to replace
        // it - so we render the real FooterComponent in the footer outlet to restore it.
        loadComponent: () => import('src/app/header-content-container.component').then(m => m.HeaderContentContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/producer-contract-signing/producer-contract-signing.component').then(m => m.ProducerContractSigningComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/components/footer/footer.component').then(m => m.FooterComponent),
                outlet: 'footer'
            }
        ]
    },

    {
        path: 'producerContractRedirect',
        // Must be MultiOutletContainerComponent (not EmptyComponent, whose template is empty): the children
        // below render into named 'content'/'sideRail' outlets, so an EmptyComponent parent never mounts
        // ProducerContractRedirectComponent → blank frame (issue #53, same antipattern as #49).
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/producer-contracts/producer-contract-redirect/producer-contract-redirect.component').then(m => m.ProducerContractRedirectComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'carrierViewOfProducerRedirect',
        loadComponent: () => import('src/components/carrier-view-of-producer-redirect/carrier-view-of-producer-redirect.component').then(m => m.CarrierViewOfProducerRedirectComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'carrierViewOfProducerRedirect',
            requiresPermission: true
        } satisfies RouteData
    },

    {
        path: 'completeCarrierRequest',
        loadComponent: () => import('src/components/complete-carrier-request/complete-carrier-request.component').then(m => m.CompleteCarrierRequestComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'completeCarrierRequest',
            requiresPermission: true
        } satisfies RouteData
    },

    {
        path: 'ProducerNotifications',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/settings/producer-notifications/producer-notifications').then(m => m.ProducerNotificationsComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'manageProducerGroup',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'manageProducerGroup',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/producer-groups/manage-producer-group/manage-producer-group.component').then(m => m.ManageProducerGroupComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'redirectRoute',
        loadComponent: () => import('src/components/redirect/redirect-route.component').then(m => m.RedirectRouteComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData
    }
];
